import React from "react";
import { CheckCircle2 } from "lucide-react";
import Jauge from "./don/Jauge";
import { fmtEUR } from "./don/euros";
import { chiffresJauge } from "../../lib/etatCampagne";

const pluriel = (mot, n) => `${mot}${n > 1 ? "s" : ""}`;

/**
 * Progression publique d'une campagne (paniers ou collecte), d'après l'état
 * calculé par le serveur (lib/etatCampagne.js) :
 *   - rien de promis encore → « Objectif X · soyez le premier à donner »
 *     au lieu d'une barre vide qui se lirait « la collecte ne démarre pas » ;
 *   - objectif atteint → « Objectif atteint grâce à vous » (les dons restent
 *     ouverts, le pourcentage continue au-delà de 100 %) ;
 *   - sans objectif → le total, sans jauge.
 * Les réservations de places n'ont pas de jauge (le parent ne l'affiche pas).
 * Montants promis arrondis VERS LE BAS, comme le pourcentage : jamais
 * « 1 000 € / 1 000 € · 99 % » pour 999,60 € promis.
 */
const euros = (v) => fmtEUR(Math.floor(Number(v) || 0));

export default function BlocProgression({ campaign, etat }) {
  const stats = campaign.stats || {};
  const unites = campaign.type === "unit_qty";
  const { valeur, objectif, pourcentage } = chiffresJauge(campaign, etat);
  const atteint = objectif != null
    && (etat?.fourni ? Boolean(etat.objectif_atteint) : valeur >= objectif);
  const label = campaign.unit_label || "unité";
  const donateurs = stats.donors_count || 0;

  if (valeur <= 0) {
    return (
      <div className="mb-8 text-center" data-testid="progression-premier-don">
        <p className="text-base text-stone-600">
          {objectif != null ? (
            <>
              <strong className="font-semibold text-stone-900 tabular-nums">
                Objectif {unites ? `${objectif} ${pluriel(label, objectif)}` : fmtEUR(objectif)}
              </strong>
              {" · soyez le premier à donner"}
            </>
          ) : "Soyez le premier à donner"}
        </p>
      </div>
    );
  }

  const objectifAtteint = atteint && (
    <p className="mb-2 text-sm font-medium text-emerald-700 inline-flex items-center gap-1.5"
      data-testid="objectif-atteint">
      <CheckCircle2 className="h-4 w-4" />
      Objectif atteint grâce à vous
    </p>
  );

  if (unites) {
    return (
      <div className="mb-8">
        <div className="flex justify-between items-baseline mb-2">
          <p className="text-2xl font-semibold text-stone-900 tabular-nums">
            {valeur}
            {objectif != null && <span className="text-stone-400 text-lg"> / {objectif}</span>}
            <span className="text-sm text-stone-500 ml-1.5">{pluriel(label, valeur)}</span>
          </p>
          <p className="text-sm text-stone-500 tabular-nums">{euros(stats.committed)}</p>
        </div>
        {objectifAtteint}
        {objectif != null && <Jauge pourcentage={pourcentage} teinte="or" />}
        {donateurs > 0 && (
          <p className="text-xs text-stone-500 mt-2">
            {donateurs} {pluriel("donateur", donateurs)}
          </p>
        )}
      </div>
    );
  }

  // Collecte en euros
  return (
    <div className="mb-8">
      <div className="flex justify-between items-baseline mb-2">
        <p className="text-2xl font-semibold text-stone-900 tabular-nums">{euros(valeur)}</p>
        {objectif != null && (
          <p className="text-sm text-stone-500 tabular-nums">/ {fmtEUR(objectif)}</p>
        )}
      </div>
      {objectifAtteint}
      {/* Sans objectif, pas de jauge : une barre vide en permanence se lirait
          comme « la collecte ne démarre pas ». Le total collecté suffit. */}
      {objectif != null && <Jauge pourcentage={pourcentage} teinte="navy" />}
      {donateurs > 0 && (
        <p className="text-xs text-stone-500 mt-2">
          {donateurs} {pluriel("donateur", donateurs)}
          {objectif != null && ` · ${pourcentage}% de l'objectif`}
        </p>
      )}
    </div>
  );
}
